import { DatePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ComplaintService } from '../../core/services/complaint.service';
import { OpsService } from '../../core/services/ops.service';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent {
  private readonly ops = inject(OpsService);
  private readonly complaintsSvc = inject(ComplaintService);

  readonly analytics = this.ops.analytics;
  readonly message = signal<string | null>(null);

  officer = '';
  department = 'All';

  /** Which case's card is expanded — only one at a time, small-card-grid style. */
  readonly expandedCase = signal<string | number | null>(null);

  toggleCase(id: string | number): void {
    this.expandedCase.update((current) => (current === id ? null : id));
  }

  /** The citizen who filed the underlying grievance — cases don't carry this themselves. */
  citizenFor(c: { trackingId: string | number }): string {
    const complaint = this.complaintsSvc.getById(c.trackingId);
    return complaint?.citizenName || complaint?.complainantName || 'Unknown citizen';
  }

  readonly performance = this.ops.officerPerformance;

  private static readonly ROLE_LABELS: Record<string, string> = {
    officer: 'Officer',
    peshkar: 'Peshkar',
  };

  /** Cycled by card index so the officer overview grid reads as distinctly colorful, not role-dependent. */
  private static readonly CARD_ACCENTS = ['#1d4ed8', '#c2410c', '#15803d', '#6d28d9', '#0e7490', '#be185d', '#b45309'];

  roleLabel(role: string): string {
    return ReportsComponent.ROLE_LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1);
  }

  accentColor(index: number): string {
    return ReportsComponent.CARD_ACCENTS[index % ReportsComponent.CARD_ACCENTS.length];
  }

  /** Jump straight to an officer's detailed view from their overview card. */
  selectOfficerCard(name: string): void {
    this.officer = name;
    this.expandedCase.set(null);
  }

  /** Departments actually represented among current officers — 'All' plus whatever real desks exist. */
  departmentOptions(): string[] {
    const set = new Set(this.performance().map((row) => row.department));
    return ['All', ...Array.from(set).sort()];
  }

  /** Officer overview rows narrowed to the selected department. */
  filteredPerformance() {
    const dept = this.department;
    return dept === 'All' ? this.performance() : this.performance().filter((row) => row.department === dept);
  }

  /** Each option names the officer AND their role, so picking one doesn't require a separate legend. */
  officerOptions(): { officer: string; label: string }[] {
    return this.filteredPerformance().map((row) => ({
      officer: row.officer,
      label: `${row.officer} (${this.roleLabel(row.role)})`,
    }));
  }

  /** The one officer picked from the filter — null until a name is chosen. */
  selectedOfficer() {
    if (!this.officer) return null;
    return this.performance().find((row) => row.officer === this.officer) ?? null;
  }

  /** Every case assigned to the selected officer — their deadline and filed report live on each row. */
  officerCases() {
    const row = this.selectedOfficer();
    if (!row) return [];
    return this.ops
      .caseList()
      .filter((c) => c.assignedOfficerRef != null && String(c.assignedOfficerRef) === String(row.id))
      .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime());
  }

  /** Drives the deadline pill's color — overdue only matters while the case is still open. */
  deadlineTone(c: { status: string; investigationDueAt?: string | null }): 'overdue' | 'soon' | 'set' | 'none' {
    if (!c.investigationDueAt) return 'none';
    if (c.status === 'Closed') return 'set';
    const due = new Date(c.investigationDueAt).getTime();
    if (Number.isNaN(due)) return 'none';
    const days = (due - Date.now()) / 86_400_000;
    if (days < 0) return 'overdue';
    if (days <= 3) return 'soon';
    return 'set';
  }

  resolutionRate(row: { load: number; resolved: number }): number {
    if (!row.load) return 0;
    return Math.round((row.resolved / row.load) * 100);
  }

  initials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  barWidth(value: number, max: number): number {
    return Math.round((value / max) * 100);
  }

  exportPdf(): void {
    // PDF export is not wired yet — avoid a misleading success toast.
  }

  exportExcel(): void {
    // Excel export is not wired yet — avoid a misleading success toast.
  }
}
